import { Token } from "./TokenEnum";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { GlobalStore } from "./GlobalStore";
import { TokenMetadata } from "./getTokenMetadata";
import * as Sentry from "@sentry/browser";
import { Amount } from "../../types/amount";
import { Chain } from "@rainbow-me/rainbowkit";
import { fetchBalancesFast } from "./getBalancesFast";
import { cloneDeep } from "lodash-es";
import { SENTRY_CONTEXTS } from "../../config/consts";
import { transformPricesToBalances } from "../../utils/tokenMetadataUtils";
import { getIsUserConnected } from "utils/userUtils";

export type Balances = Array<Balance>;

export interface Balance {
  symbol: Token;
  balance: Amount;
}

export const getBalances = createAsyncAction(
  async ({
    address,
    tokenMetadata,
    chain,
  }: {
    address: `0x${string}`;
    tokenMetadata: TokenMetadata;
    chain: Chain;
  }) => {
    try {
      Sentry.configureScope(scope => {
        scope.setTransactionName(SENTRY_CONTEXTS.BALANCES);
      });
      const result = {
        chain: chain,
        balances: await fetchBalancesFast(address, tokenMetadata, chain),
      };
      if (result) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not get balances`, { extra: { event: e } });
      console.error(`Could not get balances`, e);
      return errorResult([], `Could not get balances`, e);
    }
    Sentry.captureException(`Could not get balances`);
    console.error(`Could not get balances`);
    return errorResult([], `Couldn't get balances`);
  },
  {
    postActionHook: ({ result }) => {
      const resultClone = cloneDeep(result);
      if (!resultClone.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          if (s.address) {
            if (resultClone.payload.chain.id !== s.network.id) {
              console.log(
                `Got balances for ${resultClone.payload.chain.id} but was expecting ${s.network.id}`
              );
              return;
            }
            console.log(`Successfully got balances for ${s.network.id}`);
            s.balances = resultClone.payload.balances;
          }
        });
      }
    },
  }
);

export const clearBalances = () => {
  GlobalStore.update(s => {
    if (s.tokens) {
      s.balances = transformPricesToBalances(s.tokens);
    }
  });
};
